import React from 'react';
import {
    Box,
    TextField,
    Button,
    Typography,
    MenuItem,
    IconButton,
    Card,
    CardContent,
    Avatar,
    CardHeader,
    Grid,
    Divider,
    Alert
} from '@mui/material';
import { 
    Add, 
    Delete, 
    Security as SecurityIcon,
    Person as PersonIcon,
    AdminPanelSettings as AdminIcon
} from '@mui/icons-material';

interface RolePermission {
    role: string;
    permissionLevel: 'READ' | 'WRITE' | 'ADMIN';
}

interface StandaloneRolePermissionManagerProps {
    title?: string;
    subtitle?: string;
    helperText?: string;
    value: RolePermission[];
    onChange: (rolePermissions: RolePermission[]) => void;
    disabled?: boolean;
}

const StandaloneRolePermissionManager: React.FC<StandaloneRolePermissionManagerProps> = ({
    title = "Roles y Permisos",
    subtitle = "Asignación de roles y niveles de permisos",
    helperText = "Los roles determinan qué usuarios pueden acceder y qué acciones pueden realizar.",
    value,
    onChange,
    disabled = false
}) => {
    const handleRoleChange = (index: number, field: keyof RolePermission) => (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        const updatedPermissions = [...value];
        updatedPermissions[index] = {
            ...updatedPermissions[index],
            [field]: e.target.value
        };
        onChange(updatedPermissions);
    };

    const addRolePermission = () => {
        onChange([...value, { role: '', permissionLevel: 'READ' }]);
    };

    const removeRolePermission = (index: number) => {
        if (value.length > 1) {
            const updatedPermissions = value.filter((_, i) => i !== index);
            onChange(updatedPermissions);
        }
    };

    // Opciones de permisos
    const permissionOptions = [
        { value: 'READ', label: 'Solo Lectura' },
        { value: 'WRITE', label: 'Lectura y Escritura' },
        { value: 'ADMIN', label: 'Administrador' }
    ];

    return (
        <Card sx={{ mb: 3, width: '100%', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', borderRadius: 3 }}>
            <CardHeader
                avatar={
                    <Avatar sx={{ bgcolor: '#FF9800' }}>
                        <SecurityIcon />
                    </Avatar>
                }
                title={title}
                subheader={subtitle}
            />
            <CardContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {helperText}
                </Typography>

                {value.map((permission, index) => (
                    <Box key={index} sx={{ mb: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                        <Grid container spacing={2} alignItems="flex-start">
                            <Grid item xs={12} sm={5}>
                                <TextField
                                    fullWidth
                                    label={`Nombre del Rol ${index + 1} *`}
                                    value={permission.role}
                                    onChange={handleRoleChange(index, 'role')}
                                    disabled={disabled}
                                    required
                                    helperText="Escriba el nombre del rol (ej: Administrador, Usuario, Supervisor)"
                                    sx={{
                                        '& .MuiInputBase-root': {
                                            borderRadius: 2,
                                        },
                                    }}
                                    placeholder="Ej: Administrador, Supervisor, Operador"
                                />
                            </Grid>
                            
                            <Grid item xs={10} sm={5}>
                                <TextField
                                    fullWidth
                                    select
                                    label="Nivel de Permiso"
                                    value={permission.permissionLevel}
                                    onChange={handleRoleChange(index, 'permissionLevel')}
                                    disabled={disabled}
                                    sx={{
                                        '& .MuiInputBase-root': {
                                            borderRadius: 2,
                                        },
                                    }}
                                >
                                    {permissionOptions.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            
                            <Grid item xs={2} sm={2}>
                                {value.length > 1 && (
                                    <IconButton
                                        onClick={() => removeRolePermission(index)}
                                        disabled={disabled}
                                        color="error"
                                        sx={{ mt: 1 }}
                                    >
                                        <Delete />
                                    </IconButton>
                                )}
                            </Grid>
                        </Grid>
                    </Box>
                ))}
                
                <Button
                    startIcon={<Add />}
                    onClick={addRolePermission}
                    disabled={disabled}
                    size="small"
                    sx={{ borderRadius: 2 }}
                >
                    Agregar Rol
                </Button>

                <Divider sx={{ my: 2 }} />

                <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                        <strong>Niveles de Permisos:</strong>
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1 }}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                px: 1,
                                py: 0.5,
                                borderRadius: 1,
                                backgroundColor: 'rgba(76, 175, 80, 0.1)',
                            }}
                        >
                            <PersonIcon sx={{ fontSize: 16, color: '#4CAF50' }} />
                            <Typography variant="caption" sx={{ fontWeight: 500 }}>
                                Solo Lectura
                            </Typography>
                        </Box>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                px: 1,
                                py: 0.5,
                                borderRadius: 1,
                                backgroundColor: 'rgba(255, 152, 0, 0.1)',
                            }}
                        >
                            <SecurityIcon sx={{ fontSize: 16, color: '#FF9800' }} />
                            <Typography variant="caption" sx={{ fontWeight: 500 }}>
                                Lectura y Escritura
                            </Typography>
                        </Box>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                px: 1,
                                py: 0.5,
                                borderRadius: 1,
                                backgroundColor: 'rgba(244, 67, 54, 0.1)',
                            }}
                        >
                            <AdminIcon sx={{ fontSize: 16, color: '#F44336' }} />
                            <Typography variant="caption" sx={{ fontWeight: 500 }}>
                                Administrador
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    Los roles determinan qué usuarios pueden acceder a esta entidad,
                    mientras que los niveles de permisos definen qué acciones pueden realizar.
                </Typography>
            </CardContent>
        </Card>
    );
};

export default StandaloneRolePermissionManager;